// /src/pages/CategoryList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaTags,
  FaPlus,
  FaPowerOff,
} from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import toast from "react-hot-toast";

import {
  useGetAllCategoriesQuery,
  useActivateCategoryMutation,
  useDeactivateCategoryMutation,
} from "../toolkitServices/categoryApi";
import { useDebounce } from "../hooks/useDebounced";
import { useSelector } from "react-redux";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};


const CategoryList = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // search + debounce
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // status filter -> boolean
  const [statusFilter, setStatusFilter] = useState(""); // "", "Active", "Inactive"
  const activeBool = useMemo(() => {
    if (statusFilter === "Active") return true;
    if (statusFilter === "Inactive") return false;
    return undefined;
  }, [statusFilter]);

  // ✅ mobile filter modal
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // server params
  const qParams = useMemo(
    () => ({ page, limit, search: debouncedSearch || "", active: activeBool }),
    [page, limit, debouncedSearch, activeBool]
  );

  const { data, isLoading, isFetching, error } = useGetAllCategoriesQuery(qParams);

  const [activateCategory, actState] = useActivateCategoryMutation();
  const [deactivateCategory, deactState] = useDeactivateCategoryMutation();

  // shortcut: Ctrl+P => new category
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/masters/category-master-list/new-category");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // ✅ whenever filters change, go page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const busyToggle = actState.isLoading || deactState.isLoading;

  const onToggleActive = async (row) => {
    try {
      if (!row?._id) return;
      if (row.isActive) {
        await deactivateCategory(row._id).unwrap();
        toast.success("Category deactivated");
      } else {
        await activateCategory(row._id).unwrap();
        toast.success("Category activated");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.data?.msg || e?.data?.message || "Action failed");
    }
  };

  // ✅ chips
  const chips = useMemo(() => {
    const arr = [];
    if (search?.trim()) arr.push({ key: "search", label: `Search: ${search.trim()}` });
    if (statusFilter) arr.push({ key: "status", label: `Status: ${statusFilter}` });
    return arr;
  }, [search, statusFilter]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    if (key === "status") setStatusFilter("");
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  const paginationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 6,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgba(148,129,206,.25)"
        : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
    menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
    option: (base, state) => ({
      ...base,
      padding: "6px 8px",
      backgroundColor: state.isSelected
        ? "#9481ce"
        : state.isFocused
          ? "#ede9fe"
          : "white",
      color: state.isSelected ? "white" : "#334155",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* 🔄 Global overlay loader */}
      {isLoading && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading categories…</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaTags className="text-sm" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-[15px] font-semibold tracking-tight sm:text-lg">
                Categories
              </h2>
              <span className="text-[11px] font-semibold text-violet-100/90">
                {isFetching ? "refreshing…" : `(${total})`}
              </span>
            </div>
            <p className="hidden truncate text-[11px] text-violet-50 sm:block">
              Manage product categories, status, HSN and descriptions
            </p>
          </div>
          {isFetching && rows.length > 0 && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> updating…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden lg:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/masters/category-master-list/new-category"
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full"
          >
            <FaPlus className="text-[10px]" />
            <span className="hidden sm:inline">Add New</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </div>
      </div>

      {/* Filters bar
          ✅ Mobile: Search + Filters button
          ✅ Desktop: Search + Status inline + Filters button
      */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        {/* MOBILE */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            type="text"
            placeholder="Search name/slug/hsn…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
          />
          <button
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>
        </div>

        {/* DESKTOP */}
        <div className="hidden items-end gap-2 sm:flex">
          {/* Status */}
          <div className="flex w-[150px] flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex min-w-[240px] flex-1 flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">
              Search
            </label>
            <input
              type="text"
              placeholder="Search name/slug/hsn…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Filters button (parity) */}
          <div className="flex w-auto flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex h-9 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
              title="Advanced filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="shrink-0 border-b bg-red-50 px-3 py-1.5 text-xs text-red-600 sm:px-4">
          {error?.data?.message || "Failed to load categories"}
        </div>
      )}

      {/* Chips */}
      {chips.length > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border bg-white px-2 py-1 text-[11px] shadow-sm"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto flex-shrink-0 text-[11px] text-[#6b59c6] hover:underline"
            onClick={resetFilters}
            title="Reset all"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table (✅ flex-1 like SalesList) */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[920px] text-left text-xs sm:text-sm">
          <thead className="sticky top-0 z-10 bg-[#efeafd] text-[10px] uppercase text-[#4b3d91] shadow-sm sm:text-[11px]">
            <tr>
              <th className="px-3 sm:px-4 py-1.5">#</th>
              <th className="px-3 sm:px-4 py-1.5">Name</th>
              <th className="px-3 sm:px-4 py-1.5">Short</th>
              <th className="px-3 sm:px-4 py-1.5">Slug</th>
              <th className="px-3 sm:px-4 py-1.5">HSN</th>
              <th className="px-3 sm:px-4 py-1.5">Status</th>
              <th className="px-3 sm:px-4 py-1.5">Description</th>
              <th className="sticky right-0 z-30 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-1.5 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                <span className="sm:hidden">Act</span>
                <span className="hidden sm:inline">Action</span>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {!isLoading && rows?.length ? (
                rows.map((item, index) => {
                  const busyRow =
                    busyToggle && (actState?.originalArgs === item?._id || deactState?.originalArgs === item?._id);

                  return (
                    <motion.tr
                      key={item?._id || index}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, delay: index * 0.01 }}
                      className={`border-t border-slate-100 transition hover:bg-[#f7f5ff] ${
                        busyRow ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-3 sm:px-4 py-2.5 font-medium">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="px-3 sm:px-4 py-1.5 max-w-[220px]">
                        <span className="block truncate" title={item?.name || "-"}>
                          {item?.name || "-"}
                        </span>
                      </td>

                      <td className="px-3 sm:px-4 py-1.5 max-w-[180px]">
                        <span className="block truncate" title={item?.shortName || "-"}>
                          {item?.shortName || "-"}
                        </span>
                      </td>

                      <td className="px-3 sm:px-4 py-1.5 max-w-[220px]">
                        <span className="block truncate" title={item?.slug || "-"}>
                          {item?.slug || "-"}
                        </span>
                      </td>

                      <td className="px-3 sm:px-4 py-1.5">
                        {item?.hsn || "-"}
                      </td>

                      <td className="px-3 sm:px-4 py-1.5">
                        <span
                          className={[
                            "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                            item?.isActive
                              ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                              : "border-rose-100 bg-rose-50 text-rose-700",
                          ].join(" ")}
                        >
                          {item?.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="px-3 sm:px-4 py-1.5 max-w-[320px]">
                        <span className="line-clamp-2">
                          {item?.description || "-"}
                        </span>
                      </td>

                      <td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-1.5 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px] sm:px-2">
                        <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          onClick={() =>
                            navigate(`/masters/category-master-list/${item?._id}/edit`)
                          }
                          title="Edit"
                        >
                          <FaPencilAlt />
                        </motion.button>

                        {/* Delete (UI same; integrate mutation later) */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="order-3 inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Delete"
                          onClick={() => {
                              if (canDelete(user)) {
                                toast("Delete API not wired here yet")
                              }else{
                              toast.error("You don't have permission to delete");
                              }
                          
                          }}
                        >
                          <FaTrashAlt />
                        </motion.button>

                        <button
                          className={[
                            "order-2 inline-flex h-6 w-6 items-center justify-center rounded-md border text-[10px] disabled:opacity-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]",
                            item?.isActive
                              ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                              : "border-emerald-100 text-emerald-600 hover:bg-emerald-50",
                          ].join(" ")}
                          onClick={() => onToggleActive(item)}
                          disabled={busyToggle}
                          title={item?.isActive ? "Deactivate" : "Activate"}
                        >
                          {busyRow ? <Spinner size={12} /> : <FaPowerOff />}
                        </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              ) : !isLoading ? (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td colSpan="8" className="text-center text-gray-500 py-6">
                    No matching records found.
                  </td>
                </motion.tr>
              ) : null}
            </AnimatePresence>

            {isLoading && rows.length === 0 && (
              <tr>
                <td colSpan="8" className="py-10 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50/60 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{pages}</span> • {total ?? 0}{" "}
          record(s)
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page <= 1 || isFetching || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{
                  value: pages ? page : 1,
                  label: String(pages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, pages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, pages)}</span>
            </span>

            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select
                value={{
                  value: pages ? page : 1,
                  label: String(pages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, pages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[58px]"
                aria-label="Select page number"
              />
              <span>of {Math.max(1, pages)}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page >= pages || isFetching || isLoading}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Filters Modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between bg-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    Advanced filters
                  </h3>
                  <p className="mt-0.5 text-[11px] text-violet-100">
                    Filter categories by status and search term.
                  </p>
                </div>
                <button
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="space-y-4 overflow-y-auto p-4 text-[11px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                    >
                      <option value="">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-slate-600 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Search name/slug/hsn…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="rounded-full bg-[#6b59c6] px-4 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-[#5948ae]"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryList;
